import React from 'react';
import { FieldError, FieldValues, useForm, UseFormRegister, UseFormTrigger } from 'react-hook-form';
import { defineMessages, FormattedMessage } from 'react-intl';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  FORM_INPUT_COMPANY_NAME,
  FormInputCompanyName,
  companyNameRequiredValidationSchema,
  FormFieldsCompanyName,
  formFieldsCompanyNameDefaultValue,
} from '../../components/FormInputCompanyName/FormInputCompanyName';
import {
  FormInputEmail,
  emailRequiredValidationSchema,
  FormFieldsEmail,
  formFieldsEmailDefaultValue,
} from '../../components/FormInputEmail/FormInputEmail';
import {
  FormInputFirstName,
  firstNameRequiredValidationSchema,
  FormFieldsFirstName,
  formFieldsFirstNameDefaultValue,
} from '../../components/FormInputFirstName/FormInputFirstName';
import {
  FormInputMessage,
  messageRequiredValidationSchema,
  FormFieldsMessage,
  formFieldsMessageDefaultValue,
} from '../../components/FormInputMessage/FormInputMessage';
import {
  FormInputPhoneNumber,
  phoneNumberValidationSchema,
  phoneNumberRequiredValidationSchema,
  FormFieldsPhoneNumber,
  formFieldsPhoneNumberDefaultValue,
  FormInputPhoneNumberLabelName,
  FORM_INPUT_PHONE_NUMBER,
} from '../../components/FormInputPhoneNumber/FormInputPhoneNumber';
import { DELAY_ERROR } from '../../constants/forms';
import { formHooks } from '../../hooks/formHooks/formHooks';
import { CountryCode } from '../../types/apiEnums';
import { Button, ButtonFit, ButtonIntent, ButtonType } from '../Button/Button';
import { PdpUsps } from '../PdpUsps/PdpUsps';
import { Title } from '../Title/Title';
import styles from './BusinessOfferFormV1.module.css';

const BUSINESS_OFFER_FORM = 'business-offer-form';

export type FormFields =
  FormFieldsCompanyName
  & FormFieldsFirstName
  & FormFieldsEmail
  & FormFieldsPhoneNumber
  & FormFieldsMessage;

const businessOfferFormDefaultValues: FormFields = {
  ...formFieldsCompanyNameDefaultValue,
  ...formFieldsFirstNameDefaultValue,
  ...formFieldsEmailDefaultValue,
  ...formFieldsPhoneNumberDefaultValue,
  ...formFieldsMessageDefaultValue,
};

export type BusinessOfferValidationSchemaAndLabelsResult = {
  businessOfferValidationSchema: yup.AnyObjectSchema;
  phoneNumberLabel: FormInputPhoneNumberLabelName;
};

export type BusinessOfferFormV1Params = {
  businessOfferValidationSchema: yup.AnyObjectSchema;
  countryCode: CountryCode | null;
  isFocusToTheFirstField?: boolean;
  onSubmit: (fields: FormFields, phoneCode: string) => void;
  phoneCode: string;
};

type BusinessOfferFormV1Result = {
  errors: {
    companyName?: FieldError;
    email?: FieldError;
    firstName?: FieldError;
    message?: FieldError;
    phoneNumber?: FieldError;
  };
  handleSubmit: () => void;
  handleSubmitData: (fields: FormFields) => void;
  isDirty: boolean;
  isSubmitButtonDisabled: boolean;
  register: UseFormRegister<FormFields>;
  setFocusToTheFirstField: () => void;
  trigger: UseFormTrigger<FormFields>;
};

type HandleFirstFieldBlurParams = {
  isFocusToTheFirstField?: boolean;
  resetFocusToTheFirstField?: () => void;
};

export const callbacks = {
  getBusinessOfferValidationSchemaAndLabels(
    applicationBuild: ApplicationBuild,
  ): BusinessOfferValidationSchemaAndLabelsResult {
    const businessOfferValidationSchema = yup.object().shape({
      companyName: companyNameRequiredValidationSchema,
      firstName: firstNameRequiredValidationSchema,
      email: emailRequiredValidationSchema,
      phoneNumber: applicationBuild === 'CBBE'
        ? phoneNumberRequiredValidationSchema
        : phoneNumberValidationSchema,
      message: messageRequiredValidationSchema,
    });

    const phoneNumberLabel = applicationBuild === 'CBBE'
      ? FormInputPhoneNumberLabelName.phoneNumberRequiredLabel
      : FormInputPhoneNumberLabelName.phoneNumberLabel;

    return {
      businessOfferValidationSchema,
      phoneNumberLabel,
    };
  },
};

export const hooks = {
  useBusinessOfferValidationSchemaAndLabels(applicationBuild: ApplicationBuild) {
    return React.useMemo(() =>
      callbacks.getBusinessOfferValidationSchemaAndLabels(applicationBuild), [applicationBuild]);
  },

  useBusinessOfferFormV1({
    businessOfferValidationSchema,
    onSubmit,
    isFocusToTheFirstField,
    countryCode,
    phoneCode,
  }: BusinessOfferFormV1Params): BusinessOfferFormV1Result {
    const {
      register,
      formState: {
        isValid,
        errors,
        isDirty,
      },
      handleSubmit: handleSubmitForm,
      reset,
      setFocus,
      trigger,
    } = useForm<FormFields>({
      context: { countryCode, phoneCode },
      resolver: yupResolver(businessOfferValidationSchema),
      mode: 'all',
      delayError: DELAY_ERROR,
      defaultValues: businessOfferFormDefaultValues,
    });

    const setFocusToTheFirstField = React.useCallback(() => {
      setFocus(FORM_INPUT_COMPANY_NAME);
    }, [setFocus]);

    const handleSubmitData = React.useCallback(async (fields: FormFields) => {
      const { companyName, firstName, email, message } = fields;

      if (companyName && firstName && email && message && isValid) {
        onSubmit(fields, phoneCode);
        reset();
      }
    }, [isValid, onSubmit, reset, phoneCode]);

    React.useEffect(() => {
      if (isFocusToTheFirstField) {
        setFocusToTheFirstField();
      }
    }, [setFocusToTheFirstField, isFocusToTheFirstField]);

    return {
      register,
      errors,
      handleSubmitData,
      handleSubmit: handleSubmitForm(handleSubmitData),
      isSubmitButtonDisabled: !isValid,
      setFocusToTheFirstField,
      trigger,
      isDirty,
    };
  },

  useHandleFirstFieldBlur({
    isFocusToTheFirstField,
    resetFocusToTheFirstField,
  }: HandleFirstFieldBlurParams) {
    return React.useCallback(() => {
      if (isFocusToTheFirstField && resetFocusToTheFirstField) {
        resetFocusToTheFirstField();
      }
    }, [isFocusToTheFirstField, resetFocusToTheFirstField]);
  },
};

const messages = defineMessages({
  submit: {
    id: 'src.components.BusinessOfferFormV1.submit',
    defaultMessage: 'Send',
  },
});

export type Props = {
  isDesktop: boolean;
  isFocusToTheFirstField?: boolean;
  isPDPBusinessModalUsps: boolean;
  onSubmit: (fields: FormFields, phoneCode: string) => void;
  resetFocusToTheFirstField?: () => void;
  submitInProgress: boolean;
  subtitle: string | null;
  title: string | null;
};

export const BusinessOfferFormV1 = React.memo((props: Props) => {
  const {
    onSubmit,
    resetFocusToTheFirstField,
    submitInProgress,
    isFocusToTheFirstField = false,
    title,
    subtitle,
    isDesktop,
    isPDPBusinessModalUsps,
  } = props;

  const {
    phoneCode,
    countryCode,
    handleCountryChange,
  } = formHooks.useHandleCountryChange();

  const {
    businessOfferValidationSchema,
    phoneNumberLabel,
  } = hooks.useBusinessOfferValidationSchemaAndLabels(CONFIG.applicationBuild);

  const {
    register,
    errors,
    handleSubmit,
    isSubmitButtonDisabled,
    trigger,
    isDirty,
  } = hooks.useBusinessOfferFormV1({
    onSubmit,
    businessOfferValidationSchema,
    isFocusToTheFirstField,
    countryCode,
    phoneCode,
  });

  const handleFirstFieldBlur = hooks.useHandleFirstFieldBlur({
    isFocusToTheFirstField,
    resetFocusToTheFirstField,
  });

  return (
    <div className={styles.businessOfferBlock}>
      {title
        ? <Title
          title={title}
          subTitle={subtitle}
          className={styles.title}
          subTitleClassName={styles.subTitle}
        />
        : null
      }

      {isPDPBusinessModalUsps && !isDesktop && <PdpUsps />}

      <form
        id={BUSINESS_OFFER_FORM}
        onSubmit={handleSubmit}
        className={styles.businessOfferForm}
      >
        <div className={styles.inputs}>
          <div className={styles.inputWrapper}>
            <FormInputCompanyName
              {...register(FORM_INPUT_COMPANY_NAME, {
                onBlur: handleFirstFieldBlur,
              })}
              error={errors.companyName}
              labelClassName={styles.inputLabel}
            />
          </div>
          <div className={styles.inputWrapper}>
            <FormInputFirstName
              {...register('firstName')}
              error={errors.firstName}
              labelClassName={styles.inputLabel}
            />
          </div>
          <div className={styles.inputWrapper}>
            <FormInputEmail
              {...register('email')}
              error={errors.email}
              labelClassName={styles.inputLabel}
            />
          </div>
          <div className={styles.inputWrapper}>
            <FormInputPhoneNumber
              {...register(FORM_INPUT_PHONE_NUMBER)}
              error={errors.phoneNumber}
              labelContainerClassname={styles.labelContainerClassname}
              labelClassName={styles.inputLabel}
              labelName={phoneNumberLabel}
              phoneCode={phoneCode}
              onCountryChange={handleCountryChange}
              prefillCountryCode={countryCode}
              trigger={trigger as unknown as UseFormTrigger<FieldValues>}
              isFormDirty={isDirty}
            />
          </div>
        </div>
        <FormInputMessage
          {...register('message')}
          error={errors.message}
          labelClassName={styles.inputLabel}
          inputClassName={styles.textarea}
        />
        <Button
          className={styles.submitButton}
          intent={isPDPBusinessModalUsps ? ButtonIntent.primaryCheckOutV2 : ButtonIntent.primary}
          type={ButtonType.Submit}
          fit={ButtonFit.Inline}
          loading={submitInProgress}
          disabled={isSubmitButtonDisabled}
        >
          <FormattedMessage {...messages.submit} />
        </Button>
      </form>
    </div>
  );
});

BusinessOfferFormV1.displayName = 'BusinessOfferFormV1';
